import React, { useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { PREVIEW_EMAIL_TEMPLATE } from "../../../../utils/GraphQL/queries";
import "./emailPreview.css";
import Auth from "../../../../utils/auth";

// Email templates Phase 1 (2026-09-16). This component no longer carries a
// React copy of the email copy. It asks the server to render the SAME
// template the send path uses (previewEmailTemplate -> renderEmail) and shows
// the returned document in an iframe, so the office previews exactly what the
// customer receives. The first request fires immediately; later ones are
// debounced 350 ms on the composer inputs; a request id drops any response
// that arrives after a newer request was fired.
//
// The unsubscribe easter egg (eleven escalating alerts, then a logout on the
// twelfth click) is kept: the server tags the preview's unsubscribe anchor
// with data-bdr-unsub and a click listener on the iframe document counts it.

const unsubResponses = [
  "Why are you trying to unsubscribe? You're sending the email!",
  "Seriously?",
  "You're still trying?",
  "Stop it please...",
  "Come on, no more. Thank you!",
  "I said no more!",
  "You better stop!",
  "I'm warning you....",
  "It's pointless to unsubscribe. It doesn't work for you. So stop it.",
  "STOP IT!!!",
  "Dang it. I warned you! You click it again and you're gonna have consequences!",
];

const DEBOUNCE_MS = 350;
const GENERIC_ERROR = "Preview unavailable right now.";

// SingleClient passes invoiceLink as a STRING ("" or one URL); the quote flow
// passes arrays that are rebuilt on every render. Normalize, then key the
// effect on the JSON form so identical content never refires the request.
const toLinkArray = (links) => {
  if (links === undefined || links === null) return [];
  const arr = Array.isArray(links) ? links : [links];
  return arr
    .filter((l) => typeof l === "string" && l.trim().length > 0)
    .map((l) => l.trim());
};

const EmailPreview = ({
  text,
  previewType,
  toFirstName,
  quoteLinks,
  invoiceLinks,
  receivedPhoto,
  from,
}) => {
  const frameRef = useRef(null);
  const requestIdRef = useRef(0);
  // Read inside the effect without being a dependency: the first request
  // must not wait for the debounce, later ones must. Flipped on the first
  // SETTLED response, success or error, so a failing first request (deploy
  // restart, rate limit) does not turn every keystroke into an immediate hit.
  const hasRespondedRef = useRef(false);
  const [html, setHtml] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [frameHeight, setFrameHeight] = useState(0);
  const [previewError, setPreviewError] = useState("");
  const [unsubCounter, setUnsubCounter] = useState(0);
  const [fetchPreview] = useLazyQuery(PREVIEW_EMAIL_TEMPLATE, {
    fetchPolicy: "no-cache",
  });

  const quoteKey = JSON.stringify(toLinkArray(quoteLinks));
  const invoiceKey = JSON.stringify(toLinkArray(invoiceLinks));

  useEffect(() => {
    if (!previewType) {
      return undefined;
    }

    const delay = hasRespondedRef.current ? DEBOUNCE_MS : 0;

    const timer = setTimeout(async () => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      try {
        const result = await fetchPreview({
          variables: {
            type: previewType,
            text: text || "",
            quoteLink: JSON.parse(quoteKey),
            invoiceLink: JSON.parse(invoiceKey),
            toFirstName: toFirstName || "",
            receivedPhoto: !!receivedPhoto,
            from: from || "",
          },
        });

        // A newer request was fired while this one was in flight.
        if (requestId !== requestIdRef.current) {
          return;
        }
        hasRespondedRef.current = true;

        const preview = result?.data?.previewEmailTemplate;
        if (result?.error || !preview) {
          // Coded validation errors (e.g. the 20,000-character cap) keep
          // their text through the server's error formatter; uncoded ones
          // arrive as its generic sentence. Either is safe to show.
          setPreviewError(result?.error?.message || GENERIC_ERROR);
          return;
        }

        setPreviewError("");
        setHtml(preview.html || "");
        setWarnings(Array.isArray(preview.warnings) ? preview.warnings : []);
      } catch (err) {
        if (requestId === requestIdRef.current) {
          hasRespondedRef.current = true;
          setPreviewError(err?.message || GENERIC_ERROR);
        }
      }
    }, delay);

    return () => clearTimeout(timer);
    // fetchPreview is stable for the component's life; the inputs are the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, previewType, toFirstName, quoteKey, invoiceKey, receivedPhoto, from]);

  useEffect(() => {
    if (unsubCounter > 11) {
      window.alert("You done did it now! Hahaha");
      logoutHandler();
      return;
    }
    const num = unsubCounter - 1;
    if (unsubCounter > 0) {
      window.alert(unsubResponses[num]);
    }
  }, [unsubCounter]);

  const logoutHandler = async () => {
    const loggedOut = await Auth.logout();

    if (loggedOut) {
      window.location.reload();
    }
  };

  // srcdoc documents share the parent origin (sandbox keeps allow-same-origin
  // and nothing else, so no script inside can run). Each srcDoc change is a
  // NEW document, so the listener is attached on every load.
  const handleFrameLoad = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) {
      return;
    }
    try {
      // The html element's box is the content height. documentElement's
      // scrollHeight is NOT: in standards mode it is at least the viewport
      // (our own inline height), so it would ratchet up on every load.
      const box = doc.documentElement?.getBoundingClientRect();
      const measured = box ? Math.ceil(box.height) : 0;
      if (measured > 0) {
        setFrameHeight((prev) =>
          Math.abs(prev - (measured + 16)) > 2 ? measured + 16 : prev
        );
      }
      doc.addEventListener("click", (event) => {
        const anchor =
          event.target && event.target.closest
            ? event.target.closest("a")
            : null;
        if (!anchor) {
          return;
        }
        event.preventDefault();
        if (anchor.hasAttribute("data-bdr-unsub")) {
          setUnsubCounter((prev) => prev + 1);
        }
      });
    } catch (err) {
      // Defensive only: a same-origin srcdoc document is always readable.
    }
  };

  const waitingForFirstDocument = !html && !previewError;

  return (
    <div className="eml-preview-container">
      {waitingForFirstDocument && (
        <p className="eml-preview-loading">Rendering preview...</p>
      )}
      <iframe
        ref={frameRef}
        className={`eml-preview-frame${previewError ? " eml-preview-frame-errored" : ""}`}
        title="Email preview"
        sandbox="allow-same-origin"
        srcDoc={html}
        style={{
          height: frameHeight > 0 ? `${frameHeight}px` : undefined,
          display: waitingForFirstDocument ? "none" : "block",
        }}
        onLoad={handleFrameLoad}
      />
      {previewError && (
        <p className="eml-preview-error" role="alert">
          {previewError}
        </p>
      )}
      {!previewError && warnings.length > 0 && (
        <ul className="eml-preview-warnings">
          {warnings.map((w, index) => (
            <li key={`eml-preview-warning-${index}`}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EmailPreview;
